import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const gassinEn: ChauffeurPage = {
  slug: "gassin",
  title: "VTC & private chauffeur in Gassin",
  subtitle:
    "Private chauffeur in Gassin: one of France’s Most Beautiful Villages above Pampelonne, Nice airport transfers and hourly hire.",
  keywords: [
    "vtc gassin",
    "private chauffeur gassin",
    "gassin airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Gassin",
    subtitle:
      "One of France’s Most Beautiful Villages, above Pampelonne: Nice airport transfer, hourly hire and long distance, 7 days a week.",
    keywords: [
      "VTC Gassin",
      "Pampelonne beaches chauffeur",
      "Nice airport transfer",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Gassin (Gulf of Saint-Tropez)",
    badges: [
      "Hilltop village & Pampelonne",
      "Nice airport transfer",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Gassin",
    items: [
      {
        title: "Hilltop village handled",
        description:
          "Narrow lanes (including l’Androuno) and limited parking at the top of the village: the chauffeur drops you as close as possible and returns on request.",
      },
      {
        title: "Above Pampelonne",
        description:
          "A commanding position between the village and the Pampelonne beaches: beach shuttles with no parking worry.",
      },
      {
        title: "Long-distance transfer",
        description:
          "From Nice airport, around 1h20 to 1h30 of road handled in high-end comfort.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Gassin",
    services: [
      {
        title: "Nice airport transfer",
        text: "Gassin ⇄ Nice Côte d’Azur airport with flight tracking; a longer run than from Cannes, timed at departure.",
      },
      {
        title: "Saint-Raphaël TGV station",
        text: "Pick-up at Saint-Raphaël station, around 30 to 40 minutes away, as a rail alternative.",
      },
      {
        title: "Pampelonne beach shuttles",
        text: "To the Pampelonne beach clubs (neighbouring Ramatuelle) and back, with no parking constraint.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day for the village, the vineyards, the beaches and Saint-Tropez.",
      },
      {
        title: "Long distance",
        text: "Trips of any distance from Gassin: Cannes, Nice, Monaco and beyond, with a dedicated chauffeur.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Gassin village",
        description:
          "Hilltop village listed among France’s Most Beautiful Villages, lanes and panorama.",
      },
      {
        name: "L’Androuno",
        description: "One of France’s narrowest lanes, at the village’s heart.",
      },
      {
        name: "Pampelonne beaches",
        description: "Beach clubs below, on neighbouring Ramatuelle.",
      },
      {
        name: "Vineyards",
        description: "Vineyards on the gulf’s slopes, around the village.",
      },
      {
        name: "Saint-Raphaël station",
        description: "TGV station around 30-40 min away, a transfer point.",
      },
      {
        name: "Nice airport",
        description: "Nice Côte d’Azur airport, around 1h20-1h30 away.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur for Gassin",
    subheading:
      "From the hilltop village to the Pampelonne beaches, a premium service in the Gulf of Saint-Tropez.",
    paragraphs: [
      "Gassin, listed among France’s Most Beautiful Villages, crowns a peak above Pampelonne: narrow lanes — including the famous Androuno —, a panorama over the gulf and the vineyards, and scarce parking at the top. A chauffeur who drops you close to the pedestrian entrance spares you the hunt for a space and manoeuvring in the alleys.",
      "The village has no airport: arrivals mostly come through Nice Côte d’Azur, around 1h20 to 1h30 away depending on summer traffic. We set the departure time to limit waiting, and offer Saint-Raphaël’s TGV station, some thirty minutes away, as an alternative transfer point.",
      "Below, the Pampelonne beaches draw crowds all summer: one chauffeur runs the beach shuttles, village dinners, vineyard visits and nearby Saint-Tropez, with discretion and flexible timing. Fixed fares or hourly hire: an immediate quote.",
    ],
    bullets: [
      "Drop-off close to the village",
      "Pampelonne beach shuttles",
      "Nice airport & Saint-Raphaël station transfers",
    ],
  },
  process: {
    heading: "Book a chauffeur in Gassin",
    steps: [
      "Tell us your route (village, Pampelonne, airport, station) and times.",
      "We recommend the right vehicle and fare.",
      "The chauffeur tracks your flight or train and adjusts departure.",
      "Direct transfer or hourly hire across the gulf during your stay.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Gassin"),
    {
      q: "How long is the drive to Nice airport from Gassin?",
      a: "Around 1h20 to 1h30 outside peak times, more on summer weekends and traffic peaks in the gulf. We adjust the departure and track your flight.",
    },
    {
      q: "Do you serve Saint-Raphaël TGV station?",
      a: "Yes. Saint-Raphaël station is around 30 to 40 minutes from Gassin; it is a handy alternative transfer point for train arrivals and departures.",
    },
    {
      q: "Do you handle the link with Saint-Tropez?",
      a: "Yes, Saint-Tropez is very close; we run round trips to the port, restaurants and nights out, with safe late-night returns.",
    },
    {
      q: "How far ahead should I book?",
      a: "In high summer season, book as early as possible: demand is strong across the Gulf of Saint-Tropez. We also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele and villa stays.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes sedan, premium van (V-Class), SUV and minibus depending on group size and luggage, suited to the long run from Nice or Cannes.",
    },
    {
      q: "Do you run shuttles to the Pampelonne beaches?",
      a: "Yes, from Gassin we serve the Pampelonne beach clubs (on neighbouring Ramatuelle) as round trips, with no parking constraint and flexible timing in summer.",
    },
  ],
  relatedLinks: [
    {
      label: "Private chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Private chauffeur in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private driver on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Rent a car in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Gassin",
    text: "Nice airport transfer, Saint-Raphaël station or a shuttle to Pampelonne: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Gassin — car service & airport transfer",
    description:
      "Private car service in Gassin: Nice airport transfer, Saint-Raphaël TGV station, Pampelonne beach shuttles and the Saint-Tropez link.",
    canonical: "/en/chauffeur/gassin",
  },
};

export default gassinEn;
